import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  ChatProvider,
  ChatRoot,
  ChatHeader,
  ChatMessageList,
  ChatInput,
  ChatConnectionStatus,
  ChatSendError,
  ChatFloatingWrapper,
  useChatContext,
  registerCustomElement,
} from '@almai1/chatbot-widget'
import { useTranslation } from 'react-i18next'
import { LeadForm, ProgramCard, SchoolCard, AccommodationCard, useLocaleSync } from '@local/shared-components'

// Wrapper for LeadForm that injects context values
const LeadFormWrapper: React.FC<{ data?: unknown }> = ({ data }) => {
  const { sessionId, sessionKey, config, handleSseEvent, locale } = useChatContext()
  // Extract language code from locale (e.g., 'it-IT' -> 'it')
  const language = locale?.split('-')[0] || 'en'
  return (
    <LeadForm
      data={data}
      apiBaseUrl={config.apiBaseUrl}
      sessionId={sessionId}
      sessionKey={sessionKey}
      onSseEvent={handleSseEvent}
      language={language}
    />
  )
}

// Register custom elements
registerCustomElement('lead_form', LeadFormWrapper)
registerCustomElement('program_card', ProgramCard)
registerCustomElement('school_card', SchoolCard)
registerCustomElement('accommodation_card', AccommodationCard)

// Default API URL (can be overridden via prop)
const DEFAULT_API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:50020/api'

// Graduation cap icon for the header (education theme)
function GraduationCapIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  )
}

// Chat bubble icon for empty state
function ChatBubbleIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      <path d="M8 9h8M8 13h4"/>
    </svg>
  )
}

function RestartButton() {
  const { createNewSession, isTyping } = useChatContext()
  const { t } = useTranslation()

  const handleRestart = async () => {
    if (confirm(t('widget.restartConfirm'))) {
      await createNewSession()
    }
  }

  return (
    <button
      className="widget-restart-btn"
      onClick={handleRestart}
      disabled={isTyping}
      title={t('widget.restartButton')}
      aria-label={t('widget.restartButton')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 4v6h6" />
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      </svg>
    </button>
  )
}

function CloseButton() {
  const { setIsOpen } = useChatContext()
  const { t } = useTranslation()

  return (
    <button
      className="widget-close-btn"
      onClick={() => setIsOpen(false)}
      title={t('widget.closeButton', 'Close')}
      aria-label={t('widget.closeButton', 'Close')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
      </svg>
    </button>
  )
}

function ChatInputWithError() {
  const { t } = useTranslation()
  const [restoredValue, setRestoredValue] = useState<string | undefined>(undefined)

  const handleError = useCallback((content: string | null) => {
    if (content) {
      setRestoredValue(content)
    }
  }, [])

  const handleRestoredValueApplied = useCallback(() => {
    setRestoredValue(undefined)
  }, [])

  return (
    <div className="widget-input-area">
      <ChatSendError
        className="widget-send-error"
        messageClassName="widget-send-error-message"
        closeButtonClassName="widget-send-error-close"
        progressClassName="widget-send-error-progress"
        errorMessage={t('widget.sendFailed')}
        onError={handleError}
      />
      <ChatInput
        className="widget-input-container"
        formClassName="widget-input-form"
        textareaClassName="widget-textarea"
        sendButtonClassName="widget-send-btn"
        audioButtonClassName="widget-audio-btn"
        restoredValue={restoredValue}
        onRestoredValueApplied={handleRestoredValueApplied}
        sendButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        }
        audioButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        }
        audioRecordingContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
        }
      />
    </div>
  )
}

function LocaleSync() {
  const { locale } = useChatContext()
  useLocaleSync(locale)
  return null
}

function TypingIndicator() {
  const { t } = useTranslation()
  const { isTyping, isTranscribing, timeline } = useChatContext()

  const toolStatus = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i] as { type?: string; data?: { statusMessage?: string } }
      if (item.type === 'toolStatus') {
        return item.data?.statusMessage as string | undefined
      }
    }
    return undefined
  }, [timeline])

  if (!isTyping && !isTranscribing) return null

  return (
    <div className="widget-activity-indicator">
      {toolStatus ? (
        <div className="activity-indicator-with-spinner">
          <svg className="activity-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" opacity="0.3" />
            <path d="M12 2v4" />
          </svg>
          <span className="activity-indicator-content">{toolStatus}</span>
        </div>
      ) : (
        <div className="widget-typing-globe">
          <div className="globe-container">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <ellipse cx="12" cy="12" rx="10" ry="4" />
              <path d="M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
            </svg>
            <span className="orbit-dot" />
          </div>
          <span className="widget-typing-label">{t('widget.typing')}</span>
        </div>
      )}
    </div>
  )
}

export type WidgetMode = 'embedded' | 'popup'

interface ChatWidgetProps {
  apiUrl?: string
  mode?: WidgetMode
}

function EmptyState() {
  const { t } = useTranslation()

  return (
    <div className="widget-empty-state">
      <div className="widget-empty-icon">
        <ChatBubbleIcon />
      </div>
      <h3>{t('widget.welcomeTitle')}</h3>
      <p>{t('widget.welcomeMessage')}</p>
    </div>
  )
}

// Icons for suggestion cards
function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.3-4.3"/>
    </svg>
  )
}

function MapPinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function MessageCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
    </svg>
  )
}

function CompassIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
  )
}

function SuggestionChips() {
  const { t } = useTranslation()
  const { sendMessage, messages } = useChatContext()

  // Show chips only if user hasn't sent any message yet
  const hasUserMessage = messages.some((msg) => msg.isUser)

  if (hasUserMessage) {
    return null
  }

  const suggestions = [
    { label: t('widget.starterPrograms'), icon: <SearchIcon /> },
    { label: t('widget.starterDestinations'), icon: <MapPinIcon /> },
    { label: t('widget.starterQuestions'), icon: <MessageCircleIcon /> },
    { label: t('widget.starterUnsure'), icon: <CompassIcon /> },
  ]

  return (
    <div className="widget-suggestions">
      <div className="widget-suggestions-grid">
        {suggestions.map(({ label, icon }) => (
          <button
            key={label}
            type="button"
            className="widget-suggestion-chip"
            onClick={() => sendMessage(label)}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function ChatContent({ mode = 'embedded' }: { mode?: WidgetMode }) {
  const { t } = useTranslation()
  const { setIsOpen } = useChatContext()

  // Close popup on Escape key
  useEffect(() => {
    if (mode !== 'popup') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mode, setIsOpen])

  return (
    <ChatRoot className="widget-root">
      <ChatHeader className="widget-header">
        <div className="widget-header-content">
          <div className="widget-header-logo">
            <GraduationCapIcon />
          </div>
          <div className="widget-header-text">
            <h1>{t('widget.headerTitle')}</h1>
            <p>{t('widget.headerSubtitle')}</p>
          </div>
        </div>
        <div className="widget-header-actions">
          <RestartButton />
          {mode === 'popup' && <CloseButton />}
          <ChatConnectionStatus
            className="widget-connection-status"
            showWhenReady={false}
            noSessionText={t('widget.offline')}
          />
        </div>
      </ChatHeader>

      <ChatMessageList
        className="widget-messages"
        emptyState={<EmptyState />}
      />
      <SuggestionChips />

      <TypingIndicator />

      <ChatInputWithError />
    </ChatRoot>
  )
}

function PopupLauncherContent() {
  const { t } = useTranslation()
  return (
    <>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        <path d="M8 9h8M8 13h4"/>
      </svg>
      <span className="widget-popup-launcher-label">{t('widget.launcherLabel', 'Crea il tuo preventivo')}</span>
    </>
  )
}

function ChatWidgetInner({ mode = 'embedded' }: { mode?: WidgetMode }) {
  if (mode === 'popup') {
    return (
      <>
        <LocaleSync />
        <ChatFloatingWrapper
          position="bottom-right"
          offset={{ x: 20, y: 80 }}
          launcherClassName="widget-popup-launcher"
          panelClassName="widget-popup-panel"
          closeButtonClassName="widget-popup-close"
          showCloseButton={false}
          launcherContent={<PopupLauncherContent />}
        >
          <ChatContent mode="popup" />
        </ChatFloatingWrapper>
      </>
    )
  }

  return (
    <>
      <LocaleSync />
      <ChatContent mode="embedded" />
    </>
  )
}

export function ChatWidget({ apiUrl, mode = 'embedded' }: ChatWidgetProps) {
  const { t } = useTranslation()
  const baseUrl = apiUrl || DEFAULT_API_URL

  return (
    <ChatProvider
      config={{
        apiBaseUrl: baseUrl,
        persistSession: true,
        sessionTTL: 8,
        enableAudio: true,
        showTimestamps: true,
        showToolStatus: false,
        placeholder: t('widget.inputPlaceholder'),
      }}
    >
      <ChatWidgetInner mode={mode} />
    </ChatProvider>
  )
}
